import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Shield, AlertTriangle } from "lucide-react";
import { SMSCodeInput } from "./SMSCodeInput";
import { twoFactorService } from "@/services/twoFactorService";
import { getCurrentSessionId, getBrowserName, getOperatingSystem } from "@/services/sessionService";
import { isMobileDevice } from "@/utils/deviceUtils";

interface TwoFactorVerificationDialogProps {
  open: boolean;
  userId: string;
  onVerified: () => void;
  onCancel: () => void;
}

export const TwoFactorVerificationDialog = ({ 
  open, 
  userId, 
  onVerified, 
  onCancel 
}: TwoFactorVerificationDialogProps) => {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [method, setMethod] = useState<'totp' | 'sms' | null>(null);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');

  const MAX_ATTEMPTS = 5;

  // Fetch user's 2FA settings when dialog opens (no longer fetches secret)
  useEffect(() => {
    if (open && userId) {
      fetchTwoFactorSettings();
    }
  }, [open, userId]);

  const fetchTwoFactorSettings = async () => {
    try {
      // Only fetch non-sensitive fields - secret is never exposed to client
      const { data, error }: any = await supabase
        .from('user_2fa')
        .select('enabled, method')
        .eq('user_id', userId)
        .single();

      if (error) throw error;
      
      console.log('[2FA] user_2fa settings', { userId, enabled: data?.enabled, method: data?.method });
      
      if (data?.enabled) {
        setMethod(data.method || 'totp');
        console.log('[2FA] method set to', data.method || 'totp');
        // SMS is already sent by auth context/hook before dialog opens
        // User can use "Resend Code" if needed
      } else {
        onVerified();
      }
    } catch (error) {
      console.error('Error fetching 2FA settings:', error);
      setError("We couldn't load your security settings. Please try again.");
    }
  };

  // Server-side TOTP verification via edge function
  const verifyTOTPCode = async (inputCode: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-totp-code', {
        body: { code: inputCode }
      });

      if (error) {
        console.error('[2FA] TOTP verification error:', error);
        return false;
      }

      return data?.valid === true;
    } catch (error) {
      console.error('[2FA] Error calling verify-totp-code:', error);
      return false;
    }
  };

  const handleVerifyCode = async (fromAutoSubmit = false) => {
    if (code.length !== 6) {
      if (!fromAutoSubmit) {
        setError('Please enter a complete 6-digit code');
      }
      return;
    }

    console.log('[2FA] Verifying code', {
      userId,
      method,
      codeLength: code.length,
      attemptsBefore: attempts,
    });

    setIsLoading(true);
    setError(null);

    try {
      let isValid = false;
      
      if (method === 'sms') {
        // Verify SMS code using database RPC
        isValid = await twoFactorService.verifySMSCode(userId, code);
      } else {
        // Verify TOTP code using server-side edge function (secret never exposed to client)
        isValid = await verifyTOTPCode(code);
      }

      console.log('[2FA] Verification result', {
        userId,
        method,
        isValid,
      });

      if (isValid) {
        // If user chose to remember device, trust it
        if (rememberDevice) {
          try {
            const deviceId = getCurrentSessionId();
            const deviceInfo = {
              device_name: `${getBrowserName()} on ${getOperatingSystem()}`,
              device_type: isMobileDevice() ? 'mobile' : 'desktop',
              browser: getBrowserName(),
              os: getOperatingSystem(),
              user_agent: navigator.userAgent
            };
            
            await twoFactorService.trustDevice(userId, deviceId, deviceInfo);
            toast({
              title: "Device trusted",
              description: "You won't need 2FA on this device for 30 days",
            });
          } catch (error) {
            console.error("Failed to trust device:", error);
            // Don't block login if trust fails
          }
        }

        toast({
          title: "Verification successful",
          description: "Welcome back!",
        });

        onVerified();
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);

        if (newAttempts >= MAX_ATTEMPTS) {
          setError('Too many failed attempts. You will be signed out for security.');
          setTimeout(() => {
            onCancel();
          }, 2000);
        } else {
          setError(`Invalid code. ${MAX_ATTEMPTS - newAttempts} attempts remaining.`);
          setCode('');
        }
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setError("We couldn't verify your code. Please check it and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (value: string) => {
    setCode(value);
    setError(null);
    
    // Auto-submit when all 6 digits are entered
    if (value.length === 6) {
      console.log('[2FA] 6 digits entered, auto-submitting');
      setTimeout(() => {
        handleVerifyCode(true);
      }, 100);
    }
  };

  const handleRecoveryCodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    // Remove all whitespace and non-alphanumeric characters, take first 6
    const sanitized = pastedText
      .replace(/[^A-Za-z0-9]/g, '')
      .substring(0, 6)
      .toUpperCase();
    setRecoveryCode(sanitized);
    setError(null);
  };

  const handleVerifyRecoveryCode = async () => {
    if (recoveryCode.length !== 6) {
      setError('Recovery codes are 6 characters');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const { data: isValid, error } = await supabase.rpc('verify_recovery_code', {
        p_user_id: userId,
        p_code: recoveryCode.toUpperCase()
      });
      
      if (error) throw error;
      
      if (isValid) {
        toast({ 
          title: "Recovery code accepted", 
          description: "Welcome back!" 
        });
        onVerified();
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        
        if (newAttempts >= MAX_ATTEMPTS) {
          setError('Too many failed attempts. You will be signed out for security.');
          setTimeout(() => onCancel(), 2000);
        } else {
          setError(`Invalid recovery code. ${MAX_ATTEMPTS - newAttempts} attempts remaining.`);
        }
        setRecoveryCode('');
      }
    } catch (error) {
      console.error('Error verifying recovery code:', error);
      setError("We couldn't verify your recovery code. Please check it and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-5 w-5 text-godyspo-gold" />
            <DialogTitle>Two-Factor Authentication</DialogTitle>
          </div>
          <DialogDescription>
            {useRecoveryCode 
              ? "Enter one of your 10 recovery codes to verify your identity"
              : `Enter the 6-digit code from your ${method === 'sms' ? 'phone' : 'authenticator app'} to continue`
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!useRecoveryCode ? (
            <>
              {method === 'sms' ? (
                <SMSCodeInput
                  code={code}
                  onCodeChange={handleCodeChange}
                  onResend={async () => {
                    await (supabase as any).functions.invoke('send-2fa-sms', { body: { user_id: userId } });
                  }}
                  error={error}
                  disabled={isLoading || attempts >= MAX_ATTEMPTS}
                />
              ) : (
                <div className="flex justify-center">
                  <InputOTP
                  maxLength={6}
                  value={code}
                  onChange={handleCodeChange}
                  disabled={isLoading || attempts >= MAX_ATTEMPTS}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              )}

              <div className="flex items-center space-x-2 mt-4 mb-2">
                <Checkbox 
                  id="remember-device" 
                  checked={rememberDevice}
                  onCheckedChange={(checked) => setRememberDevice(checked === true)}
                />
                <label 
                  htmlFor="remember-device" 
                  className="text-sm text-muted-foreground cursor-pointer select-none"
                >
                  Remember this device for 30 days
                </label>
              </div>

              <div className="flex justify-end items-center pt-2">
                <Button
                  onClick={() => handleVerifyCode(false)}
                  disabled={isLoading || code.length !== 6 || attempts >= MAX_ATTEMPTS}
                  className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
                >
                  {isLoading ? "Verifying..." : "Verify"}
                </Button>
              </div>

              <div className="text-center">
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => { 
                    setUseRecoveryCode(true); 
                    setCode(''); 
                    setError(null); 
                  }}
                  disabled={isLoading}
                  className="text-sm text-muted-foreground"
                >
                  Lost access to your {method === 'sms' ? 'phone' : 'authenticator'}? Use a recovery code
                </Button>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                Enter one of your recovery codes. Each code can only be used once.
              </p>
              <Input
                placeholder="Enter recovery code"
                value={recoveryCode}
                onChange={(e) => {
                  const sanitized = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                  setRecoveryCode(sanitized);
                  setError(null);
                }}
                onPaste={handleRecoveryCodePaste}
                maxLength={6}
                className="text-center tracking-widest font-mono uppercase"
                disabled={isLoading || attempts >= MAX_ATTEMPTS}
              />
              <Button 
                onClick={handleVerifyRecoveryCode} 
                disabled={isLoading || recoveryCode.length !== 6 || attempts >= MAX_ATTEMPTS}
                className="w-full bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
              >
                {isLoading ? "Verifying..." : "Verify Recovery Code"}
              </Button>
              <Button 
                variant="link" 
                onClick={() => { 
                  setUseRecoveryCode(false); 
                  setRecoveryCode(''); 
                  setError(null); 
                }}
                disabled={isLoading}
                className="w-full"
              >
                Back to {method === 'sms' ? 'SMS code' : 'authenticator code'}
              </Button>
            </div>
          )}

          <div className="text-center pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              disabled={isLoading}
              className="text-muted-foreground"
            >
              Cancel and sign out
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
